import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

// Set the base URL for all Axios requests
axios.defaults.baseURL = import.meta.env.VITE_REACT_APP_API_URL;

// Initialize defaults.params as an empty object before assigning values
// axios.defaults.params = {
//     key: import.meta.env.VITE_REACT_APP_API_KEY,
// };

// Axios response interceptor to handle errors globally
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (axios.isCancel(error)) return Promise.reject(error);

        const axiosError = error as AxiosError;

        const isExpectedError =
            axiosError.response &&
            axiosError.response.status >= 400 &&
            axiosError.response.status < 500;

        if (!isExpectedError) {
            console.error('Unexpected error:', axiosError);
        }

        return Promise.reject(axiosError);
    }
);

/**
 * Generic function to handle HTTP GET requests.
 * @param url - The endpoint URL.
 * @param config - Optional AxiosRequestConfig object for additional configurations.
 * @returns A promise resolving to AxiosResponse with inferred response data type.
 */
function get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axios.get<T>(url, config);
}

/**
 * Generic function to handle HTTP GET requests.
 * @param id - The ID of the resource to retrieve.
 * @param url - The endpoint URL.
 * @param config - Optional AxiosRequestConfig object for additional configurations.
 * @returns A promise resolving to AxiosResponse with inferred response data type.
 */
function getById<T>(id: number | string, url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const endpoint = `${url}/${id}`;
    console.log(endpoint);
    return axios.get<T>(endpoint, config);
}

/**
 * Generic function to handle HTTP POST requests.
 * @param url - The endpoint URL.
 * @param data - The data to send in the body of the request.
 * @param config - Optional AxiosRequestConfig object for additional configurations.
 * @returns A promise resolving to AxiosResponse with inferred response data type.
 */
function post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axios.post<T>(url, data, config);
}

/**
 * Generic function to handle HTTP PUT requests.
 * @param url - The endpoint URL.
 * @param data - The data to send in the body of the request.
 * @param config - Optional AxiosRequestConfig object for additional configurations.
 * @returns A promise resolving to AxiosResponse with inferred response data type.
 */
function put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axios.put<T>(url, data, config);
}

/**
 * Generic function to handle HTTP PATCH requests.
 * @param url - The endpoint URL.
 * @param data - The data to send in the body of the request.
 * @param config - Optional AxiosRequestConfig object for additional configurations.
 * @returns A promise resolving to AxiosResponse with inferred response data type.
 */
function patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axios.patch<T>(url, data, config);
}

/**
 * Generic function to handle HTTP DELETE requests.
 * @param url - The endpoint URL.
 * @param config - Optional AxiosRequestConfig object for additional configurations.
 * @returns A promise resolving to AxiosResponse with inferred response data type.
 */
function del<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axios.delete<T>(url, config);
}

// Export the HTTP service as a clean, reusable API
const apiService = {
    get,
    getById,
    post,
    put,
    patch,
    delete: del,
};

export default apiService;
